import { OverlayModule } from '@angular/cdk/overlay';
import { CommonModule } from '@angular/common';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { ErrorHandler, NgModule } from '@angular/core';
import { FlexLayoutModule } from '@angular/flex-layout';
import { FormsModule, ReactiveFormsModule, UntypedFormBuilder } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatNativeDateModule, MatRippleModule } from '@angular/material/core';
import {
  MAT_DIALOG_DEFAULT_OPTIONS,
  MatDialogConfig,
  MatDialogModule
} from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatListModule } from '@angular/material/list';
import { MatMenuModule } from '@angular/material/menu';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatSelectModule } from '@angular/material/select';
import { MatTooltipModule } from '@angular/material/tooltip';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { EfpApiSdkModule } from '@ats/api-sdk';
import { AppGlobalErrorhandler, EfpAppConfigModule } from '@ats/app-config';
import { CookieInteractionService, EfpAuthComponentModule } from '@ats/auth-components';
import { EfpComponentMastheadModule } from '@ats/component-masthead';
import { EfpComponentTableFilterModule } from '@ats/component-table-filter';
import { OWL_DATE_TIME_FORMATS, OWL_DATE_TIME_LOCALE } from '@ats/component-ui/date-picker';
import { EfpPersonNamePipeModule } from '@ats/component-ui/person-name';
import { EfpPopoverModule } from '@ats/component-ui/popover';
import {
  EfactoryBaseService,
  EfpDataStorageService,
  FlagsService,
  LogEntriesService,
  OdataService,
  PropertyType,
  ValueTypeTest,
  XmlParser
} from '@ats/service-base';
import { EfactoryPubSubService } from '@ats/service-pubsub';
import { EfactorySiteService } from '@ats/service-site';
import { EfactoryUserInfoService, EfactoryUserService } from '@ats/service-user';
import { EfpServiceUserNotificationModule } from '@ats/service-user-notification';
import { AuthHttpInterceptor, AuthModule } from '@auth0/auth0-angular';
import { LOADING_BAR_CONFIG, LoadingBarModule } from '@ngx-loading-bar/core';
import { LoadingBarHttpClientModule } from '@ngx-loading-bar/http-client';
import { NewRelicInterceptor } from '../interceptors/newRelicErrorInterceptor';
import { AppComponent } from './app.component';
import { appRouting } from './app.routing';
import { ToastEncodeInterceptor, ToastInterceptor } from './interceptors/toast-interceptor';
import { TranslocoRootModule } from './transloco-root.module';

@NgModule({
  declarations: [AppComponent],
  bootstrap: [AppComponent],
  imports: [
    appRouting,
    EfpAppConfigModule,
    EfpApiSdkModule,
    AuthModule.forRoot(),
    BrowserAnimationsModule,
    BrowserModule,
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    LoadingBarHttpClientModule,
    LoadingBarModule,
    EfpComponentMastheadModule,
    EfpPopoverModule,
    EfpServiceUserNotificationModule,
    EfpPersonNamePipeModule,
    EfpAuthComponentModule,
    EfpComponentTableFilterModule,
    FlexLayoutModule,
    FormsModule,
    MatButtonModule,
    MatDialogModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatListModule,
    MatMenuModule,
    MatNativeDateModule,
    MatProgressBarModule,
    MatRippleModule,
    MatSelectModule,
    MatTooltipModule,
    OverlayModule,
    TranslocoRootModule
  ],
  providers: [
    {
      provide: ErrorHandler,
      useClass: AppGlobalErrorhandler
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AuthHttpInterceptor,
      multi: true
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: NewRelicInterceptor,
      multi: true
    },
    CookieInteractionService,
    EfpDataStorageService,
    EfactoryPubSubService,
    EfactoryBaseService,
    EfactorySiteService,
    EfactoryUserInfoService,
    EfactoryUserService,
    UntypedFormBuilder,
    LogEntriesService,
    FlagsService,
    OdataService,
    PropertyType,
    ValueTypeTest,
    XmlParser,
    { provide: LOADING_BAR_CONFIG, useValue: { latencyThreshold: 250 } },
    {
      provide: MAT_DIALOG_DEFAULT_OPTIONS,
      useValue: {
        ...new MatDialogConfig(),
        role: 'dialog',
        ariaModal: true,
        panelClass: 'efp-themed-dialog'
      } as MatDialogConfig
    },
    {
      provide: OWL_DATE_TIME_LOCALE,
      useFactory: () => {
        return (navigator.languages ? navigator.languages[0] : navigator['userLanguage']) as string;
      }
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ToastEncodeInterceptor,
      multi: true
    },
    { provide: HTTP_INTERCEPTORS, useClass: ToastInterceptor, multi: true },
    {
      provide: OWL_DATE_TIME_FORMATS,
      useFactory: () => {
        const lang: string = (
          navigator.languages ? navigator.languages[0] : navigator['userLanguage']
        ) as string;
        let retVal = {};
        switch (lang.toLowerCase().substring(0, 2)) {
          case 'en':
            retVal = {
              parseInput: null,
              fullPickerInput: {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
                hour: 'numeric',
                minute: 'numeric',
                hour12: true
              },
              datePickerInput: {
                year: 'numeric',
                month: 'short',
                day: 'numeric'
              },
              timePickerInput: {
                hour: 'numeric',
                minute: 'numeric',
                hour12: true
              },
              monthYearLabel: { year: 'numeric', month: 'short' },
              dateA11yLabel: { year: 'numeric', month: 'long', day: 'numeric' },
              monthYearA11yLabel: { year: 'numeric', month: 'long' }
            };
            break;
          default:
            retVal = {
              parseInput: null,
              fullPickerInput: {
                year: 'numeric',
                month: 'numeric',
                day: 'numeric',
                hour: 'numeric',
                minute: 'numeric',
                hour12: true
              },
              datePickerInput: {
                year: 'numeric',
                month: 'numeric',
                day: 'numeric'
              },
              timePickerInput: {
                hour: 'numeric',
                minute: 'numeric',
                hour12: true
              },
              monthYearLabel: { year: 'numeric', month: 'short' },
              dateA11yLabel: { year: 'numeric', month: 'long', day: 'numeric' },
              monthYearA11yLabel: { year: 'numeric', month: 'long' }
            };
        }
        return retVal;
      }
    },
    provideHttpClient(withInterceptorsFromDi())
  ]
})
export class AppModule {}
